import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, ArrowRight, FileText, Mail, CalendarClock } from "lucide-react";

const STEPS = [
  { icon: Mail, title: "Confirmation on its way", body: "A receipt and confirmation email are being sent to you now." },
  { icon: FileText, title: "We'll request your data", body: "Our team will email you to collect marketing spend, invoices, leads and signed-case details." },
  { icon: CalendarClock, title: "Your assessment begins", body: "Analysis starts once your information is received and confirmed; expected timing is shared after review." },
];

export default function AssessmentConfirmation() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/assessment/status/${sid}`);
        if (data.email) setEmail(data.email);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 8) timer = setTimeout(poll, 1500); else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  return (
    <div className="dark min-h-screen bg-[#0A1B2E] text-white font-body flex flex-col items-center justify-center p-6" data-testid="assessment-confirmation-page">
      <div className="w-full max-w-xl text-center">
        <Link to="/" className="inline-flex items-center gap-2.5 font-body font-bold text-xl mb-10">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-7 w-auto" />
          <span>Retain<span className="text-[#0E7C7B]">Proof</span></span>
        </Link>

        {status === "checking" && (
          <div className="flex flex-col items-center" data-testid="assessment-checking">
            <Loader2 className="h-10 w-10 animate-spin text-[#D5A44A]" />
            <p className="text-zinc-400 text-sm mt-4">Confirming your payment…</p>
          </div>
        )}

        {(status === "paid" || status === "pending") && (
          <>
            <CheckCircle2 className="h-16 w-16 mx-auto text-[#156F52]" />
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#D5A44A] mt-6">Baseline Marketing Assessment · $1,250</p>
            <h1 className="font-display text-4xl font-light mt-2" data-testid="assessment-confirmation-title">Thank you — your assessment is confirmed</h1>
            <p className="text-zinc-400 text-sm mt-4">
              {status === "paid"
                ? <>Your payment has been received{email ? <> and a confirmation was sent to <span className="text-white">{email}</span></> : ""}. Here's what happens next.</>
                : <>Your order was received. If payment is still finalizing, your confirmation email will follow shortly.</>}
            </p>

            <div className="mt-10 space-y-4 text-left">
              {STEPS.map((s, i) => (
                <div key={i} className="flex gap-4 border border-white/10 bg-white/5 p-4" data-testid={`assessment-step-${i}`}>
                  <s.icon className="h-5 w-5 text-[#D5A44A] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">{s.title}</p>
                    <p className="text-zinc-400 text-sm mt-0.5">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <Button asChild className="mt-10 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-11 px-6 font-medium group" data-testid="assessment-return-home">
              <Link to="/">Return to RetainProof <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            </Button>
            <p className="mt-6 font-mono-data text-[11px] text-zinc-600">Questions? clientcare@retainproofnow.com</p>
          </>
        )}

        {status === "error" && (
          <div data-testid="assessment-error">
            <h1 className="font-display text-3xl font-light">Something went wrong</h1>
            <p className="text-zinc-400 text-sm mt-3">We couldn't find your order reference. If you completed payment, please contact clientcare@retainproofnow.com.</p>
            <Button asChild className="mt-6 bg-[#D5A44A] text-black rounded-none h-11 px-6"><Link to="/">Return home</Link></Button>
          </div>
        )}
      </div>
    </div>
  );
}
